import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FilterOrSearchModel, LastNDays } from 'src/app/models/filter-or-search.model';
import { DateUtils } from 'src/app/utils/date.utils';

@Injectable({
  providedIn: 'root'
})
export class SearchFilterChipService {
  private temporaryChips: {key: keyof FilterOrSearchModel, value: string, orderAdded: number}[] = [];

  private searchParamChipsSubject = new BehaviorSubject<{key: keyof FilterOrSearchModel, value: string, orderAdded: number}[]>([]);
  public $searchParamChips = this.searchParamChipsSubject.asObservable();

  constructor() { }

  rebuildAllChips(filterOrSearchModel: FilterOrSearchModel) {
    this.temporaryChips = [];
    this.buildChipsFromInitialRouteParams(filterOrSearchModel);
  }

  buildChipsFromInitialRouteParams(filterOrSearchModel: FilterOrSearchModel) {
    for (const [key, value] of Object.entries(filterOrSearchModel)) {
      if (key === 'minArrestDate' || key === 'maxArrestDate') {
        // Format the date to m/d/yyyy if it's a date field
        const formattedValue = value ? DateUtils.formatDateMDYYYY(value as string) : null;
        this.addChip({ key: key as keyof FilterOrSearchModel, value: formattedValue, filterModel: filterOrSearchModel });
        continue;
      }

      this.addChip({ key: key as keyof FilterOrSearchModel, value, filterModel: filterOrSearchModel })
    }
    this.searchParamChipsSubject.next([...this.temporaryChips]);
  }

  addChip({ key, value, filterModel }: { key: keyof FilterOrSearchModel; value: any; filterModel?: FilterOrSearchModel }): void {
    const excludedKeys = new Set(['sortBy', 'searchAlias']);

    if (value === null || value === undefined || value === '')
      return;

    // Skip 'sortBy'
    if (excludedKeys.has(key))
      return;

    // For URL parameter loading with filterModel, handle date range logic
    if (filterModel) {
      const isCustomDateRange = filterModel.dateRange === LastNDays.Custom ||
                               Number(filterModel.dateRange) === 0 ||
                               String(filterModel.dateRange) === '0' ||
                               String(filterModel.dateRange) === 'Custom';

      // Skip 'dateRange' if it's Custom (we'll show min/max arrest date chips instead)
      if (key === 'dateRange' && isCustomDateRange) {
        return;
      }

      // Skip 'minArrestDate' and 'maxArrestDate' if we have a non-Custom dateRange
      if ((key === 'minArrestDate' || key === 'maxArrestDate') && !isCustomDateRange) {
        return;
      }
    }

    // 'arrestingAgencies', create a separate chip for each item
    if (key === 'arrestingAgencies' && Array.isArray(value)) {
      value.forEach((agency) => {
        // Ensure the agency name is a string (trim any surrounding spaces)
        const stringAgency = String(agency).trim();

        // Check if this agency already exists as a chip
        const exists = this.temporaryChips.some(chip => chip.key === key && chip.value === stringAgency);

        if (!exists) {
          // If it doesn't exist, push a new chip for this agency
          this.temporaryChips.push({
            key: key as keyof FilterOrSearchModel,
            value: stringAgency,
            orderAdded: this.temporaryChips.length + 1
          });
        }
      });
      return;
    }

    const existingChip = this.temporaryChips.find(chip => chip.key === key);

    if (existingChip) {
      existingChip.value = String(value);
    } else {
      this.temporaryChips.push({
        key: key,
        value: String(value),
        orderAdded: this.temporaryChips.length + 1
      });
    }
  }

  removeChipAndReorder(key: string, value: any, emitEvent: boolean): void {
    let remainingChips = [];

    if(value) {
      remainingChips = this.temporaryChips.filter(c => !(c.key === key && c.value === String(value)));
    } else {
      remainingChips = this.temporaryChips.filter(c => (c.key !== key));
    }

    if (remainingChips.length !== this.temporaryChips.length) {
      this.temporaryChips = remainingChips.map((chip, index) => ({
        ...chip,
        orderAdded: index + 1
      }));
    }

    if(emitEvent) {
      this.searchParamChipsSubject.next([...this.temporaryChips]);
    }
  }

  onSearchAndFilterClick(): void {
    this.searchParamChipsSubject.next([...this.temporaryChips]);
  }

  clearChips(): void {
    this.temporaryChips = [];
  }
}